import LegacyAccess from "@/core/interfaces/Access";

/** A mention as it is stored inside a comment body. */
export const MENTION_PATTERN = /@\[(\d+)]/g;

export interface MentionSegment {
    text: string;
    /** Set when the segment is a mention. */
    accessPointId: number | null;
    /** True when the mention names someone no longer on the roster. */
    unknown: boolean;
}

const nameOf = (members: LegacyAccess[], accessPointId: number): string | null =>
    members.find(member => member.id === accessPointId)?.user?.name ?? null;

/**
 * Split a body into plain text and mention segments, resolving each token to
 * the member's current name.
 *
 * Resolving at render time is the whole point of storing a token: a member who
 * changes name keeps every past mention correct, and one who left the pharmacy
 * is shown as such instead of silently reading as plain text.
 */
export const parseMentions = (body: string, members: LegacyAccess[]): MentionSegment[] => {
    const segments: MentionSegment[] = [];
    let cursor = 0;

    for (const match of body.matchAll(MENTION_PATTERN)) {
        const start = match.index ?? 0;

        if (start > cursor) {
            segments.push({text: body.slice(cursor, start), accessPointId: null, unknown: false});
        }

        const accessPointId = Number(match[1]);
        const name = nameOf(members, accessPointId);

        segments.push({
            text: `@${name ?? 'membre retiré'}`,
            accessPointId,
            unknown: name === null
        });

        cursor = start + match[0].length;
    }

    if (cursor < body.length) {
        segments.push({text: body.slice(cursor), accessPointId: null, unknown: false});
    }

    return segments;
};

/** A mention the writer inserted from the picker, awaiting submission. */
export interface PendingMention {
    label: string;
    accessPointId: number;
}

/**
 * Turn the text as typed into the body the server stores.
 *
 * Each mention inserted from the picker is swapped back to its token, first
 * occurrence only. A writer who edited the inserted name simply loses that
 * mention rather than seeing it point at the wrong person.
 */
export const toStoredBody = (typed: string, pending: PendingMention[]): string =>
    pending.reduce((body, mention) => {
        const needle = `@${mention.label}`;
        const at = body.indexOf(needle);

        return at === -1
            ? body
            : `${body.slice(0, at)}@[${mention.accessPointId}]${body.slice(at + needle.length)}`;
    }, typed);

/**
 * The `@query` being typed right before the caret, or null when the caret is
 * not in a mention.
 */
export const mentionQueryAt = (text: string, caret: number): string | null => {
    const match = /@([\p{L}\p{M}\- ]{0,30})$/u.exec(text.slice(0, caret));

    return match ? match[1] : null;
};
